import { Tag, TagVariants } from '../Tag/Tag';
import { Paragraph } from '../Paragraph';
import {
  CheckmarkCircleIcon,
  ExclamationmarkTriangleIcon,
  XMarkOctagonIcon,
} from '@navikt/aksel-icons';
import classes from './VarianceIndicator.module.css';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { teamAgreementOf, type TeamAgreement } from '../../domain/psychological-safety/team-result';
import { agreementTones } from '../AnalysisLayout/analysis.utils';

type VarianceLevel = {
  variant: TagVariants;
  label: string;
  icon: React.ReactNode;
  explanation: string;
};

type VarianceIndicatorProps = {
  standardDeviation: number;
  showExplanation?: boolean;
  showLabel?: boolean;
};

export const VarianceIndicator = ({
  standardDeviation,
  showExplanation = false,
  showLabel = true,
}: VarianceIndicatorProps) => {
  const { t } = useTranslation();
  const varianceLevel = getVarianceLevel(standardDeviation, t);

  return (
    <div className={classes.container}>
      <div className={classes.indicatorRow}>
        {showLabel && <span className={classes.label}>{t('teams.variance.label')}</span>}
        <Tag variant={varianceLevel.variant} size="md">
          <span className={classes.tagContent}>
            {varianceLevel.icon}
            <span>{varianceLevel.label}</span>
          </span>
        </Tag>
      </div>
      {showExplanation && (
        <Paragraph size="md" className={classes.explanation}>
          {varianceLevel.explanation}
        </Paragraph>
      )}
    </div>
  );
};

const varianceIcons: Record<TeamAgreement, React.ReactNode> = {
  HIGH: <CheckmarkCircleIcon aria-hidden />,
  MODERATE: <ExclamationmarkTriangleIcon aria-hidden />,
  LOW: <XMarkOctagonIcon aria-hidden />,
};

function getVarianceLevel(standardDeviation: number, t: TFunction): VarianceLevel {
  const level = teamAgreementOf(standardDeviation);
  return {
    variant: agreementTones[level],
    icon: varianceIcons[level],
    label: t(`teamResult.agreement.${level}`),
    explanation: t(`teamResult.agreementExplanation.${level}`),
  };
}
